import {
  hasFixedHeightV2,
  isHeightAdjustableElement,
  isImageNode,
  isRelativelyFillableElementV2,
  showingPlaceholder,
} from '@/utils/helperFunc';
import { ROOT_NODE, type EditorState, type Node, ActionMethodsWithConfig } from '@craftjs/core';
import { useCallback } from 'react';
import { useContainerHelper } from './useContainerHelper';
import { getElementDimensionsWithoutPadding } from '@/utils/numToMeasurement';
import { useCraftHelper } from './useCraftHelper';
import { type Delete, type PatchListenerAction } from '@craftjs/utils';
import { PASTE_EVENT } from '@/utils/constants';
import { useAppSelector } from '@/store/hooks';
import { selectBreakpoint } from '@/store/system/selectors';
import type { Breakpoint } from '@builder4/shared/types/layout';
import { getBreakpointProp, setBreakpointProp } from '@builder4/shared/utils/breakpointUtils';
import type { ContainerProps } from '@builder4/shared/components/Container';
export const useNormalizeHelper = () => {
  const { getFillableChilds, hasEquallyDividedSpace, getNodeWidth, getIndexInFillableChilds } =
    useContainerHelper();
  const { getChildrenExcept } = useCraftHelper();
  const breakpoint = useAppSelector(selectBreakpoint);

  /**
   * Handles the logic when an object/node is moved from one parent to another in the canvas.
   * Adjusts width, flexBasis, fillSpace, and flexGrow properties based on the target and destination containers.
   *
   * @param targetNode - The node that was moved
   * @param newParentNode - The parent node where the target was moved to
   * @param oldParentNode - The previous parent node where the target was moved from
   * @param editorState - The current state of the editor containing all nodes
   */
  const reAdjustLayoutOnParentChange = useCallback(
    (
      targetNode: Node,
      newParentNode: Node,
      editorState: EditorState,
      oldParentNode?: Node,
      bp: Breakpoint = breakpoint,
    ) => {
      // Scenarios covered in this function:
      // 1. Moving to ROOT_NODE: Set width to 100%, fillSpace to 'yes', flexGrow to 1
      // 2. Moving to container with flexDirection 'column': Set width to 100%, fillSpace to 'no', flexGrow to 1
      // 3. Moving to empty container: Set width to 100%, fillSpace to 'no', flexGrow to 1
      // 4. Moving from ROOT_NODE to non-empty row container: Divide width by 2 among all fillable children (target + existing), set fillSpace to 'yes', flexGrow to 1
      // 5. Moving from non-root to non-empty row container with equally divided space: Redistribute width equally among all fillable children, set fillSpace to 'no'
      // 6. Moving from non-root to non-empty row container with unequal space: Maintain proportional spacing based on existing DOM dimensions
      const newParentFlexDirection = getBreakpointProp(
        newParentNode.data.props as Partial<ContainerProps>,
        'flexDirection',
        bp,
      );

      if (
        newParentNode.id === ROOT_NODE ||
        newParentFlexDirection === 'column' ||
        getChildrenExcept(newParentNode.id, targetNode.id).length === 0 //because new node is added now consider it empty before this action
      ) {
        if (isRelativelyFillableElementV2(targetNode, bp)) {
          const props = targetNode.data.props;
          setBreakpointProp(props, 'width', '100%', bp, targetNode.data.displayName);
          setBreakpointProp(props, 'flexBasis', '100%', bp, targetNode.data.displayName);
          setBreakpointProp(
            props,
            'fillSpace',
            newParentNode.id === ROOT_NODE ? 'yes' : 'no',
            bp,
            targetNode.data.displayName,
          );
          setBreakpointProp(props, 'flexGrow', 1, bp, targetNode.data.displayName);
        }
      } else {
        // Case: Moving to a non-root, non-empty parent with flexDirection row
        // - Set fillSpace to 'no' for fillable elements
        if (oldParentNode && oldParentNode.id === ROOT_NODE) {
          // Coming from root node to a non-root, non-empty parent with flexDirection row
          if (isRelativelyFillableElementV2(targetNode, bp)) {
            //want to give more space to target node as its width is 100%
            // make width half of all siblings and target as well
            const allChildren = [...newParentNode.data.nodes];
            allChildren.push(targetNode.id);
            allChildren.forEach((childId) => {
              const childNode = editorState.nodes[childId];
              if (!childNode) return;
              if (!isRelativelyFillableElementV2(childNode, bp)) {
                return;
              }
              const childProps = childNode.data.props;
              const newWidth = getNodeWidth(childNode, bp) / 2;
              setBreakpointProp(
                childProps,
                'width',
                `${newWidth}%`,
                bp,
                childNode.data.displayName,
              );
              setBreakpointProp(
                childProps,
                'flexBasis',
                `${newWidth}%`,
                bp,
                childNode.data.displayName,
              );
              setBreakpointProp(childProps, 'fillSpace', 'yes', bp, childNode.data.displayName);
              setBreakpointProp(childProps, 'flexGrow', 1, bp, childNode.data.displayName);
            });
          }
        } else {
          // Coming from a non-root node to a non-root, non-empty parent with flexDirection row
          //here we need to adjust width to accomodate new node
          //if old childs are equivally divided then make all equal widths
          //else read the dom width of new node and get percentage relative to new parent and subract from others to accomodate new node
          //set fillSpace to yes and width and flexBasis to percentage of new parent
          if (isRelativelyFillableElementV2(targetNode, bp)) {
            const props = targetNode.data.props;
            setBreakpointProp(props, 'fillSpace', 'yes', bp, targetNode.data.displayName);
            const allChildrenIds = newParentNode.data.nodes;
            const children = allChildrenIds
              .map((childId) => {
                const childNode = editorState.nodes[childId];
                if (!childNode) return null;
                if (!isRelativelyFillableElementV2(childNode, bp)) return null;
                return childNode;
              })
              .filter((child) => child !== null);

            const equallyDividedSpace = hasEquallyDividedSpace(newParentNode.id, bp); // NOTE: new node will not be part during this check
            if (equallyDividedSpace) {
              //this is a case when siblings are 2 or less or equal in width
              children.forEach((child) => {
                const childNode = editorState.nodes[child.id];
                const childProps = childNode.data.props;
                const percent = `${100 / children.length}%`;
                setBreakpointProp(childProps, 'width', percent, bp, childNode.data.displayName);
                setBreakpointProp(childProps, 'flexBasis', percent, bp, childNode.data.displayName);
              });
            } else {
              //this is a case when siblings are 3 or more and not equal in width
              const targetIndex = getIndexInFillableChilds(targetNode.id, bp);
              const nextSiblingIndex = targetIndex + 1;
              const prevSiblingIndex = targetIndex - 1;
              const nextNode = children[nextSiblingIndex];
              const prevNode = children[prevSiblingIndex];
              let nodeWidth = 0;
              if (nextNode && prevNode) {
                const nextNodeWidth = getNodeWidth(nextNode, bp);
                const preNodeWidth = getNodeWidth(prevNode, bp);
                nodeWidth = (preNodeWidth + nextNodeWidth) / 3;
              } else {
                if (nextNode) {
                  nodeWidth = getNodeWidth(nextNode, bp) / 2;
                } else if (prevNode) {
                  nodeWidth = getNodeWidth(prevNode, bp) / 2;
                }
              }

              const widthValue = `${nodeWidth || 30}%`;
              setBreakpointProp(props, 'width', widthValue, bp, targetNode.data.displayName);
              setBreakpointProp(props, 'flexBasis', widthValue, bp, targetNode.data.displayName);
            }
          }
        }
      }

      // set height of incoming node placeholder
      if (newParentNode.id !== ROOT_NODE && isHeightAdjustableElement(targetNode)) {
        //check if target node should have fillSpace
        const targetProps = targetNode.data.props;
        const parentFlexDirectionForFillSpace = getBreakpointProp(
          newParentNode.data.props as Partial<ContainerProps>,
          'flexDirection',
          bp,
        );
        setBreakpointProp(
          targetProps,
          'fillSpace',
          parentFlexDirectionForFillSpace === 'row' ? 'yes' : 'no',
          bp,
          targetNode.data.displayName,
        );
        if (showingPlaceholder(targetNode)) {
          //container placeholder should be tallest child height
          const parentFlexDirectionForHeight = getBreakpointProp(
            newParentNode.data.props as Partial<ContainerProps>,
            'flexDirection',
            bp,
          );
          if (
            getChildrenExcept(newParentNode.id, targetNode.id).length === 0 ||
            parentFlexDirectionForHeight === 'row'
          ) {
            if (newParentNode.dom) {
              const { height } = getElementDimensionsWithoutPadding(newParentNode.dom);
              const parentPadding = getBreakpointProp(
                newParentNode.data.props as Partial<ContainerProps>,
                'padding',
                bp,
              ) ?? [0, 0, 0, 0];
              const parentBorderWidth =
                getBreakpointProp(
                  newParentNode.data.props as Partial<ContainerProps>,
                  'borderWidth',
                  bp,
                ) || 0;
              const newHeight =
                height - parentPadding[0] - parentPadding[1] - parentBorderWidth * 2;

              const targetHeight = getBreakpointProp(
                targetNode.data.props as Partial<ContainerProps>,
                'height',
                bp,
              );
              if (hasFixedHeightV2(targetHeight ?? '') || isImageNode(targetNode)) {
                setBreakpointProp(
                  targetProps,
                  'height',
                  newHeight,
                  bp,
                  targetNode.data.displayName,
                );
              } else {
                setBreakpointProp(
                  targetProps,
                  'placeholderHeight',
                  newHeight,
                  bp,
                  targetNode.data.displayName,
                );
              }
            }
          }
        }
      }
    },
    [
      getFillableChilds,
      hasEquallyDividedSpace,
      getIndexInFillableChilds,
      breakpoint,
      getChildrenExcept,
      getNodeWidth,
    ],
  );
  const handleObjectMovedEnd = useCallback(
    (
      state: EditorState,
      prevState: EditorState,
      actionPerformed: Delete<PatchListenerAction<typeof ActionMethodsWithConfig>, 'patches'>,
    ) => {
      const targetNodeId =
        typeof actionPerformed.params[0] === 'string'
          ? actionPerformed.params[0]
          : actionPerformed.params[0][0];
      const oldParentId = prevState.nodes[targetNodeId].data.parent as string;
      const newParentId = state.nodes[targetNodeId].data.parent as string;
      if (newParentId === oldParentId) return;
      const targetNode = state.nodes[targetNodeId];
      const oldParentNode = state.nodes[oldParentId];
      const newParentNode = state.nodes[newParentId];
      ['desktop', 'tablet', 'mobile'].forEach((bp) => {
        reAdjustLayoutOnParentChange(
          targetNode,
          newParentNode,
          state,
          oldParentNode,
          bp as Breakpoint,
        );
      });
    },
    [reAdjustLayoutOnParentChange],
  );
  const handleObjectPasted = useCallback(
    (
      state: EditorState,
      actionPerformed: Delete<PatchListenerAction<typeof ActionMethodsWithConfig>, 'patches'>,
    ) => {
      const targetNodeId = actionPerformed.params[0].rootNodeId;
      const newParentId = actionPerformed.params[1] as string;
      const targetNode = state.nodes[targetNodeId];
      const newParentNode = state.nodes[newParentId];
      ['desktop', 'tablet', 'mobile'].forEach((bp) => {
        reAdjustLayoutOnParentChange(targetNode, newParentNode, state, undefined, bp as Breakpoint);
      });
    },
    [reAdjustLayoutOnParentChange],
  );

  const handleNormalizeNode = useCallback(
    (
      state: EditorState,
      prevState: EditorState,
      actionPerformed: Delete<PatchListenerAction<typeof ActionMethodsWithConfig>, 'patches'>,
    ) => {
      switch (actionPerformed.type) {
        case 'addNodeTree': {
          const nodeTree = actionPerformed.params[0];
          if (nodeTree.eventSource === PASTE_EVENT) {
            handleObjectPasted(state, actionPerformed);
          }
          break;
        }
        case 'move':
          handleObjectMovedEnd(state, prevState, actionPerformed);
          break;
        default:
          break;
      }
    },
    [handleObjectMovedEnd],
  );
  return handleNormalizeNode;
};
